import type { EntitlementBlockKey } from 'pi-kiosk-shared/tenant-entitlements';
import type { AdminEntitlementSnapshot, EntitlementBlockPosture } from '../types/adminEntitlementSnapshot.js';

export type EntitlementRouteAction = 'read' | 'write';

function resolvePosture(
  snapshot: AdminEntitlementSnapshot | null,
  blockKey: EntitlementBlockKey,
): EntitlementBlockPosture | null {
  if (snapshot === null) {
    return null;
  }
  const block = snapshot.blocks[blockKey];
  if (block === undefined) {
    return 'DENY';
  }
  if (!block.visible || block.posture === 'HIDDEN') {
    return 'HIDDEN';
  }
  return block.posture;
}

/**
 * Nav visibility — fail-closed when snapshot is null/loading so commercial
 * surfaces (e.g. Události / `incident_centre_ui`) do not flash before /me.
 * Action helpers below stay fail-open; call sites add their own null gates.
 */
export function isEntitlementBlockNavVisible(
  snapshot: AdminEntitlementSnapshot | null,
  blockKey: EntitlementBlockKey,
): boolean {
  const posture = resolvePosture(snapshot, blockKey);
  if (posture === null) {
    return false;
  }
  return posture !== 'DENY' && posture !== 'HIDDEN';
}

export function isEntitlementBlockActionAllowed(
  snapshot: AdminEntitlementSnapshot | null,
  blockKey: EntitlementBlockKey,
  action: EntitlementRouteAction,
): boolean {
  const posture = resolvePosture(snapshot, blockKey);
  if (posture === null) {
    return true;
  }
  if (posture === 'ALLOW') {
    return true;
  }
  if (posture === 'READ_ONLY') {
    return action === 'read';
  }
  return false;
}

export function isEntitlementBlockReadOnly(
  snapshot: AdminEntitlementSnapshot | null,
  blockKey: EntitlementBlockKey,
): boolean {
  const posture = resolvePosture(snapshot, blockKey);
  return posture === 'READ_ONLY';
}
